import type Phaser from 'phaser';
import { MOWER_DEFS } from '../../content/mowers';
import { runGear, type PostedRun } from '../../meta/online/run';
import type { TileId } from '../../content/tiles';
import { dx, dy } from '../../sim/direction';
import type { SimLevel } from '../../sim/level';
import { decodeIntent } from '../../sim/replay';
import { Simulation, type LoggedIntent } from '../../sim/simulation';
import { mowerPixelPosition } from './interpolate';
import { knownPaints } from '../../meta/online/live';
import { paintedMowerTexture } from './cosmeticArt';
import { mowerTextureKey } from './placeholderArt';
import { simRulesFor } from '../../meta/rulesVersion';
import type { SimRules } from '../../sim/simRules';

/**
 * How see-through the ghost is, and its pale blue cast (a ghost, not a faded copy of yours) when
 * the run wore no paint; a painted one shows its paint (owner, 2026-11-04).
 */
const GHOST_ALPHA = 0.5;
const GHOST_TINT = 0xb8dcff;

/**
 * A ghost to race (docs/ONLINE.md, lm-wxl.2): a posted run of the same lawn, replayed in a sim of
 * its own one tick for every tick of the player's, and drawn as a see-through mower. It mows only
 * its own copy of the lawn, so it never touches the player's game.
 */
export class GhostRun {
  readonly sim: Simulation;
  private readonly intents: LoggedIntent[];
  private readonly endTick: number;
  private next = 0;
  private readonly sprite: Phaser.GameObjects.Sprite;

  /**
   * Null when the run cannot be replayed here (not one lawn, or not a mower and loadout the game
   * has). `art` is the lawn's tiles, for its tools. The ghost mows with the run's own mower,
   * upgrades and tools (a community lawn's best may have any), so it replays exactly.
   */
  static create(
    scene: Phaser.Scene,
    level: SimLevel,
    run: PostedRun,
    depth: number,
    art: readonly TileId[] = [],
  ): GhostRun | null {
    const lawn = run.lawns[0];
    if (run.lawns.length !== 1 || !lawn || !(run.mower in MOWER_DEFS)) return null;
    const gear = runGear(run, art);
    if (!gear) return null;
    try {
      const intents = lawn.inputs.map(([tick, code]) => ({ tick, intent: decodeIntent(code) }));
      // Replayed by the sim's rules on the run's own version.
      const rules = simRulesFor(run.rules);
      // Its paint, when this build knows it.
      const paint = knownPaints(run.paint ? { [run.mower]: run.paint } : undefined)[run.mower];
      const texture = paint
        ? paintedMowerTexture(scene, run.mower, paint)
        : mowerTextureKey(run.mower);
      return new GhostRun(scene, level, texture, !paint, gear, intents, lawn.endTick, depth, rules);
    } catch {
      return null;
    }
  }

  private constructor(
    scene: Phaser.Scene,
    level: SimLevel,
    texture: string,
    tinted: boolean,
    gear: NonNullable<ReturnType<typeof runGear>>,
    intents: LoggedIntent[],
    endTick: number,
    depth: number,
    rules: Readonly<SimRules>,
  ) {
    this.sim = new Simulation(level, gear.mower, {}, gear.tools, rules);
    this.intents = intents;
    this.endTick = endTick;
    this.sprite = scene.add
      .sprite(0, 0, texture, this.sim.mower.dir)
      .setAlpha(GHOST_ALPHA)
      .setDepth(depth);
    if (tinted) this.sprite.setTint(GHOST_TINT);
    this.render(0);
  }

  /** The ghost's run is over: it finished (or its recording stopped). */
  get done(): boolean {
    return this.sim.status !== 'running' || this.sim.tick >= this.endTick;
  }

  /** One tick, in step with the player's sim. */
  step(): void {
    if (this.done) return;
    const tick = this.sim.tick + 1;
    while (this.next < this.intents.length && this.intents[this.next]!.tick <= tick) {
      this.sim.enqueue(this.intents[this.next]!.intent);
      this.next++;
    }
    this.sim.step();
  }

  render(alpha: number): void {
    const m = this.sim.mower;
    const willAdvance = !this.done && !this.sim.blocks(m.x + dx(m.dir), m.y + dy(m.dir));
    const { px, py } = mowerPixelPosition(
      m,
      this.sim.crossTicks(),
      this.done ? 0 : alpha,
      willAdvance,
    );
    this.sprite.setPosition(px, py).setFrame(m.dir);
    // A finished ghost fades, parked where it ended.
    this.sprite.setAlpha(this.done ? GHOST_ALPHA / 2 : GHOST_ALPHA);
  }

  destroy(): void {
    this.sprite.destroy();
  }
}
